// Ultimate Card Game (UCG) - Battlefield Weather Rendering
//
// Extracted from js/engine/renderer.js. Mixed onto Renderer.prototype (see
// the Object.assign() call at the bottom of renderer.js) rather than kept
// as free functions, since every method here reads instance state (this.*)
// the same way every other Renderer method does - a straight cut, not a
let _rainHazeLUT = null;
function getRainHazeLUT() {
  if (_rainHazeLUT) return _rainHazeLUT;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 1;
  const hctx = c.getContext('2d');
  const g = hctx.createLinearGradient(0, 0, 128, 0);
  g.addColorStop(0, 'rgba(186, 230, 253, 0)');
  g.addColorStop(0.5, 'rgba(186, 230, 253, 0.035)');
  g.addColorStop(1, 'rgba(186, 230, 253, 0)');
  hctx.fillStyle = g;
  hctx.fillRect(0, 0, 128, 1);
  _rainHazeLUT = c;
  return _rainHazeLUT;
}

export const weatherMixin = {
  // 🌧️ & 🍃 Dynamic Multi-Layer Weather System (Rain Showers & Playful Dry-Phase Wind Gusts)
  getWeatherState() {
    const t = performance.now() * 0.001;
    const weatherPeriod = 60;  // 60s weather cycle
    const rainStart = 6;       // rain starts at 6s
    const rainDuration = 26;   // rain lasts 26s (6s to 32s)
    const windStart = 38;      // playful wind gust starts at 38s (during dry period)
    const windDuration = 14;   // wind gust lasts 14s (38s to 52s)
    const cyclePos = t % weatherPeriod;

    // Rain intensity (0.0 to 1.0)
    let rainIntensity = 0;
    if (cyclePos >= rainStart && cyclePos < rainStart + rainDuration) {
      const rAge = cyclePos - rainStart;
      rainIntensity = Math.min(1.0, Math.min(rAge / 2.0, (rainDuration - rAge) / 2.0));
    }

    // Playful Wind Gust intensity (0.0 to 1.0) - sweeps across the dry battlefield
    let windIntensity = 0;
    if (cyclePos >= windStart && cyclePos < windStart + windDuration) {
      const wAge = cyclePos - windStart;
      windIntensity = Math.min(1.0, Math.min(wAge / 2.0, (windDuration - wAge) / 2.0));
    }

    if (!this._weatherState) {
      this._weatherState = { t: 0, rainIntensity: 0, windIntensity: 0, cyclePos: 0, rainStart, rainDuration, windStart, windDuration };
    }
    const state = this._weatherState;
    state.t = t;
    state.rainIntensity = rainIntensity;
    state.windIntensity = windIntensity;
    state.cyclePos = cyclePos;
    state.rainStart = rainStart;
    state.rainDuration = rainDuration;
    state.windStart = windStart;
    state.windDuration = windDuration;
    return state;
  },

  // 🌧️ & 🍃 Layer 1: Distant Background Rain & Background Wind Streamers
  // Rendered BEHIND bases, attachments, and units for true 3D scene depth
  renderWeatherBackground(ctx, battlefield) {
    const weather = this.getWeatherState();
    const { t, rainIntensity, windIntensity } = weather;
    // Quality-tier gating (Fix 2 / PERFORMANCE_PARADIGMS.md §10): only the
    // outer `this.quality !== 'low'` renderer-call-site gate protected this
    // file before, so medium and ultra cost identically. Halve the loop
    // bounds feeding the existing batched-path-per-layer draws on medium -
    // the batching technique itself (single beginPath/stroke per layer) is
    // unchanged, only the streak/particle counts shrink.
    const isMedium = this.quality === 'medium';

    // --- 1. RAIN BACKGROUND PASS ---
    if (rainIntensity > 0.01) {
      ctx.save();

      // Atmospheric overcast blue-gray wash
      // Spans the full visible area (incl. letterbox margins beyond the
      // world border) so the wash never ends in a hard edge at world y = 0.
      ctx.fillStyle = `rgba(15, 23, 42, ${0.12 * rainIntensity})`;
      const washRect = this.getVisibleWorldRect ? this.getVisibleWorldRect() : { left: 0, top: 0, right: this.width, bottom: this.height };
      ctx.fillRect(washRect.left, washRect.top, washRect.right - washRect.left, washRect.bottom - washRect.top);

      // Distant Volumetric Rain Haze Curtains (Paradigma 8: LUT-Rasterung)
      const hazeCount = isMedium ? 2 : 3;
      const hazeLUT = getRainHazeLUT();
      if (hazeLUT) {
        ctx.save();
        ctx.globalAlpha = rainIntensity;
        for (let h = 0; h < hazeCount; h++) {
          const hProg = (t * 24 + h * 540) % (this.width + 600) - 300;
          const hOsc = Math.sin(t * 1.3 + h * 2.2) * 35;
          ctx.drawImage(hazeLUT, hProg - 180 + hOsc, 0, 360, this.height);
        }
        ctx.restore();
      }

      // Far Depth Rain Layer (Z ≈ 0.35 - Slow fall, fine streaks)
      const farCount = isMedium ? 75 : 150;
      const farVy = 430;
      const farVx = -76;
      const wrapX = this.width + 200;
      const wrapY = this.height + 120;

      ctx.strokeStyle = `rgba(147, 197, 253, ${0.28 * rainIntensity})`;
      ctx.lineWidth = 0.75;
      ctx.beginPath();

      for (let i = 0; i < farCount; i++) {
        const seedX = (i * 137.5 + i * i * 3.1) % this.width;
        const seedY = (i * 269.3 + i * 19.7) % this.height;
        const speedVar = 0.92 + ((i * 31.7) % 100) * 0.0016;
        const len = 9 + (i % 6) * 1.6;

        const curX = (((seedX + t * farVx * speedVar) % wrapX) + wrapX) % wrapX - 100;
        const curY = (((seedY + t * farVy * speedVar) % wrapY) + wrapY) % wrapY - 60;

        const dx = -len * 0.177;
        ctx.moveTo(curX, curY);
        ctx.lineTo(curX + dx, curY + len);
      }
      ctx.stroke();

      ctx.restore();
    }

    // --- 2. PLAYFUL BACKGROUND WIND STREAMERS (Behind units) ---
    if (windIntensity > 0.01) {
      ctx.save();
      const wrapX = this.width + 300;
      ctx.strokeStyle = `rgba(224, 242, 254, ${0.22 * windIntensity})`;
      ctx.lineWidth = 1.0;

      const streamerCount = isMedium ? 7 : 14;
      for (let i = 0; i < streamerCount; i++) {
        const seed = i * 67.31;
        const speed = 280 + (i % 5) * 45;
        const sx = ((seed * 19 + t * speed) % wrapX) - 150;
        const baseY = 80 + (seed * 11) % (this.height - 160);
        const wave = Math.sin(t * 3.2 + i * 1.4) * 16;
        const len = 40 + (i % 4) * 25;

        ctx.beginPath();
        ctx.moveTo(sx, baseY + wave);
        ctx.quadraticCurveTo(sx + len * 0.5, baseY + wave - 8, sx + len, baseY + wave + 4);
        ctx.stroke();
      }
      ctx.restore();
    }
  },

  // 🌧️ & 🍃 Layers 2 & 3: Midground/Foreground Weather (Rain & Playful Sweeping Wind)
  // Rendered IN FRONT of units and particle effects
  renderWeather(ctx, battlefield) {
    const weather = this.getWeatherState();
    const { t, rainIntensity, windIntensity } = weather;
    if (rainIntensity <= 0.01 && windIntensity <= 0.01) return;
    // Quality-tier gating (Fix 2): halve loop bounds on medium; see the note
    // in renderWeatherBackground() above.
    const isMedium = this.quality === 'medium';

    ctx.save();

    // ============================================================
    // 🌧️ 1. DYNAMIC PERSPECTIVE RAIN PASS
    // ============================================================
    if (rainIntensity > 0.01) {
      const wrapX = this.width + 200;
      const wrapY = this.height + 140;

      // --- MIDGROUND RAIN LAYER (Z ≈ 0.7) ---
      const midCount = isMedium ? 52 : 105;
      const midVy = 860;
      const midVx = -152;

      ctx.strokeStyle = `rgba(186, 230, 253, ${0.46 * rainIntensity})`;
      ctx.lineWidth = 1.2;
      ctx.beginPath();

      for (let i = 0; i < midCount; i++) {
        const seedX = (i * 211.9 + i * i * 5.3) % this.width;
        const seedY = (i * 383.1 + i * 23.9) % this.height;
        const speedVar = 0.9 + ((i * 43.3) % 100) * 0.002;
        const len = 20 + (i % 7) * 2.5;

        const curX = (((seedX + t * midVx * speedVar) % wrapX) + wrapX) % wrapX - 100;
        const curY = (((seedY + t * midVy * speedVar) % wrapY) + wrapY) % wrapY - 70;

        const dx = -len * 0.177;
        ctx.moveTo(curX, curY);
        ctx.lineTo(curX + dx, curY + len);
      }
      ctx.stroke();

      // --- FOREGROUND RAIN LAYER (Z ≈ 1.35) ---
      const nearCount = isMedium ? 18 : 36;
      const nearVy = 1550;
      const nearVx = -275;

      ctx.strokeStyle = `rgba(240, 249, 255, ${0.68 * rainIntensity})`;
      ctx.lineWidth = 2.0;
      ctx.beginPath();

      for (let i = 0; i < nearCount; i++) {
        const seedX = (i * 349.7 + i * i * 11.7) % this.width;
        const seedY = (i * 179.3 + i * 41.1) % this.height;
        const speedVar = 0.94 + ((i * 67.1) % 100) * 0.0012;
        const len = 48 + (i % 5) * 5.5;

        const curX = (((seedX + t * nearVx * speedVar) % wrapX) + wrapX) % wrapX - 100;
        const curY = (((seedY + t * nearVy * speedVar) % wrapY) + wrapY) % wrapY - 70;

        const dx = -len * 0.177;
        ctx.moveTo(curX, curY);
        ctx.lineTo(curX + dx, curY + len);
      }
      ctx.stroke();

      // Specular highlight droplets on near-camera streaks
      ctx.fillStyle = `rgba(255, 255, 255, ${0.72 * rainIntensity})`;
      for (let i = 0; i < nearCount; i += 2) {
        const seedX = (i * 349.7 + i * i * 11.7) % this.width;
        const seedY = (i * 179.3 + i * 41.1) % this.height;
        const speedVar = 0.94 + ((i * 67.1) % 100) * 0.0012;
        const len = 48 + (i % 5) * 5.5;

        const curX = (((seedX + t * nearVx * speedVar) % wrapX) + wrapX) % wrapX - 100;
        const curY = (((seedY + t * nearVy * speedVar) % wrapY) + wrapY) % wrapY - 70;

        const dx = -len * 0.177;
        ctx.fillRect(curX + dx - 0.8, curY + len - 1.2, 1.8, 1.8);
      }

      // --- PERSPECTIVE-OPTIMIZED GROUND IMPACT SPLASHES ---
      const laneY = battlefield ? battlefield.laneY : 200;
      const laneHeight = battlefield ? battlefield.laneHeight : 160;
      const splashCount = isMedium ? 18 : 36;
      // Constant colours + per-splash globalAlpha instead of a fresh rgba() string
      // per splash per frame (identical result: alpha multiplies the same way).
      const prevSplashAlpha = ctx.globalAlpha;
      ctx.strokeStyle = 'rgb(224, 242, 254)';
      ctx.fillStyle = 'rgb(240, 249, 255)';

      for (let i = 0; i < splashCount; i++) {
        const seed = i * 83.11;
        const sx = ((seed * 19 + t * 45) % (this.width - 120)) + 60;
        const sy = laneY + ((seed * 7 + t * 30) % laneHeight);
        const splashAge = (t * 9.5 + i * 0.37) % 1.0;

        const depthNorm = Math.max(0, Math.min(1, (sy - laneY) / Math.max(1, laneHeight)));
        const pScale = 0.55 + 0.65 * depthNorm;

        const splashW = (2.5 + splashAge * 6.5) * pScale;
        const splashH = Math.max(0.8, splashW * 0.32);
        const splashAlpha = (1.0 - splashAge) * (0.32 + 0.28 * depthNorm) * rainIntensity;

        ctx.globalAlpha = prevSplashAlpha * splashAlpha;
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.ellipse(sx, sy, splashW, splashH, 0, 0, Math.PI * 2);
        ctx.stroke();

        if (depthNorm > 0.35 && splashAge < 0.7) {
          const bProgress = splashAge / 0.7;
          const bH = Math.sin(bProgress * Math.PI) * (4 + 6 * depthNorm);
          const bDir = (i % 2 === 0 ? 1 : -1) * (1.5 + depthNorm * 2.5);
          ctx.globalAlpha = prevSplashAlpha * Math.min(1, splashAlpha * 1.2);
          ctx.fillRect(sx + bDir * bProgress, sy - bH, 1.2 * pScale, 1.2 * pScale);
        }
      }
      ctx.globalAlpha = prevSplashAlpha;
    }

    // ============================================================
    // 🍃 2. PLAYFUL SWEEPING WIND GUST PASS (Dry weather beat)
    // ============================================================
    if (windIntensity > 0.01) {
      const wrapX = this.width + 300;
      const laneY = battlefield ? battlefield.laneY : 200;
      const laneHeight = battlefield ? battlefield.laneHeight : 160;

      // 1. Playful High-Speed Curved Wind-Current Ribbons
      ctx.strokeStyle = `rgba(254, 240, 138, ${0.32 * windIntensity})`;
      ctx.lineWidth = 1.4;
      const ribbonCount = isMedium ? 11 : 22;
      for (let i = 0; i < ribbonCount; i++) {
        const seed = i * 79.43;
        const speed = 440 + (i % 6) * 60;
        const sx = ((seed * 23 + t * speed) % wrapX) - 150;
        const sy = 40 + ((seed * 13) % (this.height - 80));
        const wave = Math.sin(t * 4.2 + i * 1.6) * 18;
        const len = 35 + (i % 5) * 22;

        ctx.beginPath();
        ctx.moveTo(sx, sy + wave);
        ctx.quadraticCurveTo(sx + len * 0.4, sy + wave - 6, sx + len, sy + wave + 3);
        ctx.stroke();
      }

      // 2. Playful Tumbling Flying Leaves, Glowing Spores & Desert Sand Motes
      const leafCount = isMedium ? 21 : 42;
      // windIntensity is constant for the whole pass: build the 7 leaf/spore
      // colours once per frame, not twice per particle.
      const leafA1 = `rgba(74, 222, 128, ${0.85 * windIntensity})`;
      const leafA2 = `rgba(163, 230, 53, ${0.9 * windIntensity})`;
      const leafB1 = `rgba(186, 230, 253, ${0.9 * windIntensity})`;
      const leafB2 = `rgba(255, 255, 255, ${0.95 * windIntensity})`;
      const leafC = `rgba(250, 204, 21, ${0.75 * windIntensity})`;
      const leafD = `rgba(134, 239, 172, ${0.8 * windIntensity})`;
      for (let i = 0; i < leafCount; i++) {
        const seed = i * 53.17;
        const speed = 360 + ((i * 37) % 100) * 2.4;
        const lx = ((seed * 29 + t * speed) % wrapX) - 80;
        const baseLy = 30 + ((seed * 17) % (this.height - 60));
        const waveY = Math.sin(t * 3.8 + i * 1.7) * 22 + Math.cos(t * 2.1 + i * 0.8) * 10;
        const ly = baseLy + waveY;
        const rot = t * 6.0 + i * 2.4;

        ctx.save();
        ctx.translate(lx, ly);
        ctx.rotate(rot);

        const particleType = i % 4;
        if (particleType === 0) {
          // Vibrant Alien Leaf / Petal
          ctx.fillStyle = leafA1;
          ctx.fillRect(-3, -1.5, 6, 3);
          ctx.fillStyle = leafA2;
          ctx.fillRect(-1.5, -1, 3, 2);
        } else if (particleType === 1) {
          // Glowing Spore / Pollen Mote
          ctx.fillStyle = leafB1;
          ctx.fillRect(-1.5, -1.5, 3, 3);
          ctx.fillStyle = leafB2;
          ctx.fillRect(-0.5, -0.5, 1.5, 1.5);
        } else if (particleType === 2) {
          // Golden Sand / Dust Grain
          ctx.fillStyle = leafC;
          ctx.fillRect(-2, -1, 4, 2);
        } else {
          // Micro Cedar / Needle Wisp
          ctx.fillStyle = leafD;
          ctx.fillRect(-4, -0.75, 8, 1.5);
        }
        ctx.restore();
      }

      // 3. Ground Dust Eddies along the Lane
      const eddyCount = isMedium ? 3 : 6;
      for (let d = 0; d < eddyCount; d++) {
        const seed = d * 123.45;
        const edSpeed = 260 + d * 35;
        const edX = ((seed * 17 + t * edSpeed) % (this.width + 200)) - 100;
        const edY = laneY + 20 + ((seed * 7) % Math.max(20, laneHeight - 40));
        const spin = t * 5.5 + d * 1.8;
        const edRadius = 8 + (d % 3) * 4;

        ctx.save();
        ctx.translate(edX, edY);
        ctx.rotate(spin);
        ctx.strokeStyle = `rgba(253, 230, 138, ${0.28 * windIntensity})`;
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.arc(0, 0, edRadius, 0, Math.PI * 1.4);
        ctx.stroke();
        ctx.restore();
      }
    }

    ctx.restore();
  }
};
